'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { TOtpConfig } from './types';
import { useOtpTimer } from './useOtpTimer';

interface OtpContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  close: () => void;

  timer: number;

  otp: string;
  setOtp: (otp: string) => void;

  length: number;

  resend: {
    action: () => void;
    isPending: boolean;
    isSuccess: boolean;
  };

  confirm: {
    action: (value: string) => void;
    isPending: boolean;
    error?: string;
    reset?: () => void;
  };

  send: {
    isPending: boolean;
    isSuccess: boolean;
  };

  resendButtonRef: React.RefObject<HTMLButtonElement | null>;
  inputRef: React.RefObject<HTMLInputElement | null>;
  hasOpenedOnce: boolean;
}

const OtpContext = createContext<OtpContextValue | null>(null);

export const useOtpContext = () => {
  const ctx = useContext(OtpContext);
  if (!ctx) {
    throw new Error('OTP components must be used within <OtpProvider />');
  }
  return ctx;
};

interface OtpProviderProps {
  children: React.ReactNode;
  config: TOtpConfig;
}

export const OtpProvider = ({ children, config }: OtpProviderProps) => {
  const {
    length = 5,
    expirationInSeconds,
    expirationDate,
    resend,
    confirm,
    open: initialOpen,
    send,
    onClose,
  } = config;

  const [open, setOpen] = useState(false);
  const [otp, setOtp] = useState('');
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);

  const { timer } = useOtpTimer(expirationInSeconds, expirationDate);

  const resendButtonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  /* ---------------- actions ---------------- */

  const resetOtpState = useCallback(() => {
    setOtp('');
    confirm.reset?.();
  }, [confirm]);

  const close = useCallback(() => {
    resetOtpState();
    setOpen(false);
    onClose?.();
  }, [resetOtpState, onClose]);

  /* ---------------- effects ---------------- */

  useEffect(() => {
    if (resend.isSuccess) {
      resetOtpState();
    }
  }, [resend.isSuccess]);

  useEffect(() => {
    if (initialOpen) {
      setOpen(true);
    }
  }, [initialOpen]);

  useEffect(() => {
    if (confirm.isSuccess) {
      close();
    }
  }, [confirm.isSuccess, close]);

  useEffect(() => {
    if (open) setHasOpenedOnce(true);
  }, [open]);

  /* ---------------- controlled setOpen ---------------- */
  const setOpenControlled = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && !hasOpenedOnce && send && !send.isSuccess) return;

      if (!nextOpen) {
        close();
        return;
      }

      setOpen(nextOpen);
    },
    [hasOpenedOnce, send, close]
  );

  /* ---------------- context value ---------------- */

  const value = useMemo<OtpContextValue>(
    () => ({
      open,
      setOpen: setOpenControlled,
      close,
      timer,
      otp,
      setOtp,
      length,
      resend: {
        action: resend.action,
        isPending: Boolean(resend.isPending),
        isSuccess: Boolean(resend.isSuccess),
      },
      confirm: {
        action: confirm.action,
        isPending: Boolean(confirm.isPending),
        error: confirm.error,
        reset: confirm.reset,
      },
      send: {
        isPending: Boolean(send.isPending),
        isSuccess: Boolean(send.isSuccess),
      },
      resendButtonRef,
      inputRef,
      hasOpenedOnce,
    }),
    [
      open,
      setOpenControlled,
      close,
      timer,
      otp,
      length,
      resend.action,
      resend.isPending,
      resend.isSuccess,
      confirm.action,
      confirm.isPending,
      confirm.error,
      confirm.reset,
      send.isPending,
      send.isSuccess,
      hasOpenedOnce,
    ]
  );

  return <OtpContext.Provider value={value}>{children}</OtpContext.Provider>;
};
